// 영수증 자동출력(과제 2) 공통 타입/스키마.
// 서버(@oo/api), 매장 상주 에이전트(@oo/print-agent)가 공유한다.
//  - 서버는 주문 전환 시 PrintReceiptPayload 스냅샷을 print_jobs 에 적재한다.
//  - 에이전트는 GET /print/jobs 로 받은 PrintJobDto[] 를 ESC/POS 로 렌더해 출력하고 ack 한다.
// 이모지 금지(영수증 포함). 한글은 에이전트가 CP949(EUC-KR)로 인코딩해 보낸다.

import { z } from "zod";
import { type OrderType } from "./orders.js";
import { type ProductUnit } from "./units.js";
import { type PaymentMethodType, type PaymentStatus } from "./payment.js";

// 출력 종류.
//  - accept : 주문 수락 시 = 주방 주문표(무엇을 손질할지).
//  - ready  : 준비완료 시 = 실중량 반영 최종 영수증(받을 금액).
export const PRINT_JOB_KIND = {
  ACCEPT: "accept",
  READY: "ready",
} as const;
export type PrintJobKind = (typeof PRINT_JOB_KIND)[keyof typeof PRINT_JOB_KIND];

// 처리 상태. pending -> printing(점유) -> printed | failed.
export const PRINT_JOB_STATUS = {
  PENDING: "pending",
  PRINTING: "printing",
  PRINTED: "printed",
  FAILED: "failed",
} as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUS)[keyof typeof PRINT_JOB_STATUS];

// 영수증 항목에 붙는 추가 옵션 1종(스냅샷).
export interface PrintReceiptItemOption {
  label: string;
  // 이 옵션 1개당 추가 중량(g). 주방이 얼마나 더 손질할지 판단하는 근거.
  addGrams: number;
  quantity: number;
}

// 영수증 한 항목(스냅샷). 가격/이름/단위는 주문 시점 스냅샷이라 상품이 바뀌어도 불변.
export interface PrintReceiptItem {
  name: string;
  unit: ProductUnit;
  quantity: number;
  unitPriceKrw: number;
  // 라인 합계. accept 시엔 예상치(단가*수량 + 옵션합), ready 시엔 실중량 기준 최종치.
  lineTotalKrw: number;
  // 실중량 정산 대상 항목 여부(주문 시점 스냅샷).
  weightAdjustable: boolean;
  // 준비완료 때 입력된 실제 중량(g). 미정이면 null.
  actualWeightG: number | null;
  // 추가 옵션 목록. 옵션 도입 전 페이로드(재출력 포함)에는 없으므로 optional 이다.
  options?: PrintReceiptItemOption[];
  // 이 줄의 예상 총중량(g) = 기준중량*수량 + 옵션중량 합계. 주방 주문표에서 손질 목표를 보여준다.
  //  - 옵션 도입 전 페이로드에는 없으므로 optional.
  expectedWeightG?: number | null;
}

// 영수증 출력 스냅샷. print_jobs.payload_json 에 직렬화되어 저장되고, 에이전트가 이것만으로 렌더한다.
//  - 손님 PII(이름/연락처/주소)는 매장 내부 출력물에만 쓰이며 외부로 나가지 않는다.
export interface PrintReceiptPayload {
  // 출력 종류(렌더 분기). job.kind 와 동일값을 페이로드에도 담아 에이전트 단독 검증을 쉽게 한다.
  kind: PrintJobKind;
  // 매장/손님 구두 대조용 주문번호(YYMMDD-####).
  orderNo: string;
  type: OrderType;
  // 희망 수령 일시(없으면 ASAP). 'YYYY-MM-DD' / 'HH:MM'.
  desiredDate: string | null;
  desiredTime: string | null;
  // 손님 식별(포장/배달 공통). 배달이면 delivery 에 수령인/주소가 따로 있다.
  customerName: string | null;
  customerPhone: string | null;
  // 배달 주문일 때만. 포장이면 null.
  delivery: {
    recipientName: string;
    recipientPhone: string;
    addressLine1: string;
    addressLine2: string | null;
    postalCode: string | null;
  } | null;
  items: PrintReceiptItem[];
  customerMemo: string | null;
  // 칭찬하고 할인받기 문구(있으면). 매장 안내용.
  complimentText: string | null;
  // 금액 내역(원). ready 영수증에서 최종 결제액 산출에 쓴다.
  subtotalKrw: number;
  discountKrw: number;
  complimentDiscountKrw: number;
  deliveryFeeKrw: number;
  pointsUsedKrw: number;
  totalKrw: number;
  // 예상 최종금액(불변). accept 단계에서 "예상" 표기에 쓴다.
  estTotalKrw: number;
  // 결제 수단/상태(인앱 PG). ready 영수증의 결제 안내 문구 분기에 쓴다.
  //  - 구버전 작업(0031 이전 적재)엔 없을 수 있어, 에이전트는 미정 시 현장(onsite)/미결제(unpaid)로 처리한다.
  paymentMethodType: PaymentMethodType;
  paymentStatus: PaymentStatus;
  // 접수 시각(주문 생성)과 출력 적재 시각. ISO 8601 UTC. 에이전트가 KST 로 변환해 출력.
  orderedAt: string;
  issuedAt: string;
}

// 에이전트가 GET /print/jobs 로 받는 작업 1건.
export interface PrintJobDto {
  id: string;
  orderId: string;
  kind: PrintJobKind;
  status: PrintJobStatus;
  attempts: number;
  payload: PrintReceiptPayload;
  createdAt: string;
}

// 에이전트 ack 요청 본문. ok=true 면 printed, false 면 failed(+짧은 사유).
export const printJobAckInputSchema = z.object({
  ok: z.boolean(),
  // 실패 사유(선택). 시크릿/PII 금지. 길이 제한으로 로그 오염 방지.
  error: z.string().trim().max(300).nullable().optional(),
});
export type PrintJobAckInput = z.infer<typeof printJobAckInputSchema>;

// 에이전트 폴링 시 한 번에 가져올 작업 수 상한. 단일 매장 단일 프린터라 작게 둔다.
export const PRINT_JOBS_CLAIM_MAX = 10;
